/**
 * Step4_Riepilogo — Riepilogo ordine + consenso + Cloudflare Turnstile.
 * Mostra tutti i dati raccolti nei passi precedenti e il PricingCalculator.
 */
import React from 'react';
import { ClipboardCheck } from 'lucide-react';
import PricingCalculator from '../PricingCalculator';

export default function StepRiepilogo({
  aziendaData,
  ordineData,
  prodotto,
  prodottoNS,
  prodottoAwareness,
  prodottoMSSP,
  channelInfo,
  onAcceptTerms,
  termsAccepted,
  errors,
  turnstileWidget,
  turnstileEnabled,
}) {
  const channelLabel = {
    DIRETTO: 'Diretto',
    RESELLER: `Reseller (${channelInfo?.partner_name || ''})`,
    ASSOCIAZIONE: `Associazione (${channelInfo?.partner_name || ''})`,
  };

  const fieldError = (field) => {
    const err = errors?.[field];
    if (!err) return null;
    const msg = Array.isArray(err) ? err[0] : err;
    return <p className="text-red-500 text-xs mt-1">{msg}</p>;
  };

  const isBundle = !!ordineData._bundleId;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <ClipboardCheck className="w-6 h-6 text-yellow-600" />
        <h2 className="text-xl font-bold text-gray-900">Riepilogo Ordine</h2>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pr-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Colonna sinistra: dati */}
          <div className="space-y-4">
            {/* Azienda */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm uppercase tracking-wide">
                Azienda
              </h3>
              <dl className="text-sm space-y-1">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Nome</dt>
                  <dd className="font-medium">{aziendaData.nome_azienda || '—'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Email</dt>
                  <dd className="font-medium">{aziendaData.email || '—'}</dd>
                </div>
                {aziendaData.partita_iva && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">P.IVA</dt>
                    <dd className="font-medium">{aziendaData.partita_iva}</dd>
                  </div>
                )}
                {aziendaData.citta && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Città</dt>
                    <dd className="font-medium">{aziendaData.citta}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Prodotto */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm uppercase tracking-wide">
                Prodotto
              </h3>
              <dl className="text-sm space-y-1">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Prodotto</dt>
                  <dd className="font-medium">{prodotto?.nome_prodotto || '—'}</dd>
                </div>
                {isBundle && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Tipo</dt>
                    <dd className="font-medium text-yellow-700">Bundle</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-gray-500">Endpoint</dt>
                  <dd className="font-medium">{ordineData.numero_endpoint}</dd>
                </div>
                {ordineData.offerta_sorveglianza_rete && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Network Surveillance</dt>
                    <dd className="font-medium text-green-600">✓ Incluso</dd>
                  </div>
                )}
                {ordineData.awareness_enabled && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Awareness</dt>
                    <dd className="font-medium">{ordineData.awareness_seats} utenze</dd>
                  </div>
                )}
                {ordineData.servizio_mssp_monitoring && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Monitoraggio Cyber</dt>
                    <dd className="font-medium">{ordineData.gettoni_totali} gettoni</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Asset */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm uppercase tracking-wide">
                Asset
              </h3>
              <ul className="text-sm space-y-1">
                {(ordineData.asset || []).map((a, i) => (
                  <li key={i} className="flex justify-between">
                    <span className="text-gray-500 capitalize">{a.tipo_asset}</span>
                    <span className="font-medium">{a.asset}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Canale */}
            {channelInfo?.valid && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-2 text-sm uppercase tracking-wide">
                  Canale
                </h3>
                <p className="text-sm font-medium">
                  {channelLabel[channelInfo.channel_type] || channelInfo.channel_type}
                </p>
              </div>
            )}
          </div>

          {/* Colonna destra: pricing */}
          <div>
            <PricingCalculator
              prodotto={prodotto}
              numEndpoint={ordineData.numero_endpoint || 0}
              awarenessSeats={ordineData.awareness_seats || 0}
              awarenessEnabled={ordineData.awareness_enabled}
              prodottoAwareness={prodottoAwareness}
              nsEnabled={!!ordineData.offerta_sorveglianza_rete}
              prodottoNS={prodottoNS}
              msspEnabled={ordineData.servizio_mssp_monitoring || false}
              prodottoMSSP={prodottoMSSP}
              gettoniTotali={ordineData.gettoni_totali || 0}
              isBundle={isBundle}
            />

            {/* Termini */}
            <div className="mt-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => onAcceptTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500 accent-yellow-600"
                />
                <span className="text-sm text-gray-600">
                  Accetto i{' '}
                  <a href="/termini" target="_blank" className="text-yellow-600 underline">
                    termini e condizioni
                  </a>{' '}
                  e la{' '}
                  <a href="/privacy" target="_blank" className="text-yellow-600 underline">
                    privacy policy
                  </a>
                  .
                </span>
              </label>
              {fieldError('terms')}
            </div>

            {/* Cloudflare Turnstile */}
            {turnstileEnabled && (
              <div className="mt-4">
                {turnstileWidget}
                {fieldError('captcha')}
              </div>
            )}

            {turnstileEnabled && (
              <p className="mt-4 text-xs text-gray-400">
                Questo sito è protetto da Cloudflare Turnstile. Si applicano la{' '}
                <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="underline">
                  Privacy Policy
                </a>{' '}
                e i{' '}
                <a href="https://www.cloudflare.com/website-terms/" target="_blank" rel="noopener noreferrer" className="underline">
                  Termini di Servizio
                </a>{' '}
                di Cloudflare.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
